// Trigonometry Knowledge Graph Samples - Part 2
// Organized by difficulty levels with systematic naming

export const trigonometrySamplesPart2 = {
  harder: [
    {
      id: 'trig_hard_01',
      problem: "Find sin(π/6)",
      problemType: "trigonometry",
      difficulty: "harder",
      description: "Sine function with radian measure",
      graphData: {
        nodes: [
          {
            id: "trigonometry",
            name: "Trigonometry",
            type: "concepts",
            description: "Study of relationships between angles and sides of triangles",
            value: "Angle and side relationships",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "sine_function",
            name: "Sine Function",
            type: "concepts",
            description: "Ratio of opposite side to hypotenuse",
            value: "sin(θ) = opposite/hypotenuse",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "radian_measure",
            name: "Radian Measure",
            type: "concepts",
            description: "Angle measure in radians",
            value: "π radians = 180°",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "sine_formula",
            name: "Sine Formula",
            type: "formulas",
            description: "sin(θ) = opposite/hypotenuse",
            value: "sin(θ) = opposite/hypotenuse",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "radian_to_degree_formula",
            name: "Radian to Degree Formula",
            type: "formulas",
            description: "Convert radians to degrees: degrees = radians × (180°/π)",
            value: "degrees = radians × (180°/π)",
            importance: 9,
            x: 0, y: 50, z: 40
          },
          {
            id: "angle_pi_6",
            name: "Angle π/6",
            type: "values",
            description: "The given angle in radians",
            value: "π/6",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "degree_equivalent",
            name: "Degree Equivalent",
            type: "values",
            description: "Convert radians to degrees",
            value: "π/6 = 30°",
            importance: 8,
            x: -120, y: 150, z: -15
          },
          {
            id: "sine_pi_6_result",
            name: "Sine π/6 Result",
            type: "values",
            description: "The sine of π/6 radians",
            value: "sin(π/6) = sin(30°) = 1/2",
            importance: 9,
            x: 120, y: 150, z: -15
          },
          {
            id: "identify_radian_angle",
            name: "Identify Radian Angle",
            type: "steps",
            description: "Recognize the angle is in radians",
            value: "Step 1: Angle = π/6 radians",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "convert_to_degrees",
            name: "Convert to Degrees",
            type: "steps",
            description: "Convert π/6 to degrees",
            value: "Step 2: π/6 × (180°/π) = 30°",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_sine_formula",
            name: "Apply Sine Formula",
            type: "steps",
            description: "Use the sine formula for 30°",
            value: "Step 3: sin(30°) = 1/2",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_final_answer",
            name: "Write Final Answer",
            type: "steps",
            description: "State the final result",
            value: "Step 4: sin(π/6) = 1/2",
            importance: 9,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "trigonometry", to: "sine_function", type: "includes", strength: 9 },
          { from: "trigonometry", to: "radian_measure", type: "includes", strength: 8 },
          { from: "sine_function", to: "sine_formula", type: "expressed as", strength: 10 },
          { from: "radian_measure", to: "radian_to_degree_formula", type: "converted using", strength: 9 },
          { from: "identify_radian_angle", to: "convert_to_degrees", type: "enables", strength: 9 },
          { from: "convert_to_degrees", to: "apply_sine_formula", type: "enables", strength: 10 },
          { from: "apply_sine_formula", to: "write_final_answer", type: "leads to", strength: 9 },
          { from: "angle_pi_6", to: "identify_radian_angle", type: "identified in", strength: 8 },
          { from: "degree_equivalent", to: "convert_to_degrees", type: "result of", strength: 9 },
          { from: "sine_pi_6_result", to: "write_final_answer", type: "equals", strength: 10 },
          { from: "sine_formula", to: "apply_sine_formula", type: "implemented in", strength: 10 },
          { from: "radian_to_degree_formula", to: "convert_to_degrees", type: "implemented in", strength: 9 }
        ]
      }
    },
    {
      id: 'trig_hard_02',
      problem: "Find cos(π/4)",
      problemType: "trigonometry",
      difficulty: "harder",
      description: "Cosine function with radian measure",
      graphData: {
        nodes: [
          {
            id: "trigonometry",
            name: "Trigonometry",
            type: "concepts",
            description: "Study of relationships between angles and sides of triangles",
            value: "Angle and side relationships",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cosine_function",
            name: "Cosine Function",
            type: "concepts",
            description: "Ratio of adjacent side to hypotenuse",
            value: "cos(θ) = adjacent/hypotenuse",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "radian_measure",
            name: "Radian Measure",
            type: "concepts",
            description: "Angle measure in radians",
            value: "π radians = 180°",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "cosine_formula",
            name: "Cosine Formula",
            type: "formulas",
            description: "cos(θ) = adjacent/hypotenuse",
            value: "cos(θ) = adjacent/hypotenuse",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "radian_to_degree_formula",
            name: "Radian to Degree Formula",
            type: "formulas",
            description: "Convert radians to degrees: degrees = radians × (180°/π)",
            value: "degrees = radians × (180°/π)",
            importance: 9,
            x: 0, y: 50, z: 40
          },
          {
            id: "angle_pi_4",
            name: "Angle π/4",
            type: "values",
            description: "The given angle in radians",
            value: "π/4",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "degree_equivalent",
            name: "Degree Equivalent",
            type: "values",
            description: "Convert radians to degrees",
            value: "π/4 = 45°",
            importance: 8,
            x: -120, y: 150, z: -15
          },
          {
            id: "cosine_pi_4_result",
            name: "Cosine π/4 Result",
            type: "values",
            description: "The cosine of π/4 radians",
            value: "cos(π/4) = cos(45°) = √2/2",
            importance: 9,
            x: 120, y: 150, z: -15
          },
          {
            id: "identify_radian_angle",
            name: "Identify Radian Angle",
            type: "steps",
            description: "Recognize the angle is in radians",
            value: "Step 1: Angle = π/4 radians",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "convert_to_degrees",
            name: "Convert to Degrees",
            type: "steps",
            description: "Convert π/4 to degrees",
            value: "Step 2: π/4 × (180°/π) = 45°",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "apply_cosine_formula",
            name: "Apply Cosine Formula",
            type: "steps",
            description: "Use the cosine formula for 45°",
            value: "Step 3: cos(45°) = √2/2",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_final_answer",
            name: "Write Final Answer",
            type: "steps",
            description: "State the final result",
            value: "Step 4: cos(π/4) = √2/2",
            importance: 9,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "trigonometry", to: "cosine_function", type: "includes", strength: 9 },
          { from: "trigonometry", to: "radian_measure", type: "includes", strength: 8 },
          { from: "cosine_function", to: "cosine_formula", type: "expressed as", strength: 10 },
          { from: "radian_measure", to: "radian_to_degree_formula", type: "converted using", strength: 9 },
          { from: "identify_radian_angle", to: "convert_to_degrees", type: "enables", strength: 9 },
          { from: "convert_to_degrees", to: "apply_cosine_formula", type: "enables", strength: 10 },
          { from: "apply_cosine_formula", to: "write_final_answer", type: "leads to", strength: 9 },
          { from: "angle_pi_4", to: "identify_radian_angle", type: "identified in", strength: 8 },
          { from: "degree_equivalent", to: "convert_to_degrees", type: "result of", strength: 9 },
          { from: "cosine_pi_4_result", to: "write_final_answer", type: "equals", strength: 10 },
          { from: "cosine_formula", to: "apply_cosine_formula", type: "implemented in", strength: 10 },
          { from: "radian_to_degree_formula", to: "convert_to_degrees", type: "implemented in", strength: 9 }
        ]
      }
    },
    {
      id: 'trig_hard_03',
      problem: "Find tan(π/3)",
      problemType: "trigonometry",
      difficulty: "harder",
      description: "Tangent function with radian measure",
      graphData: {
        nodes: [
          {
            id: "trigonometry",
            name: "Trigonometry",
            type: "concepts",
            description: "Study of relationships between angles and sides of triangles",
            value: "Angle and side relationships",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "tangent_function",
            name: "Tangent Function",
            type: "concepts",
            description: "Ratio of opposite side to adjacent side",
            value: "tan(θ) = opposite/adjacent",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "radian_measure",
            name: "Radian Measure",
            type: "concepts",
            description: "Angle measure in radians",
            value: "π radians = 180°",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle_pi_3",
            name: "Angle π/3",
            type: "values",
            description: "The given angle in radians",
            value: "π/3",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "degree_equivalent",
            name: "Degree Equivalent",
            type: "values",
            description: "Convert radians to degrees",
            value: "π/3 = 60°",
            importance: 8,
            x: -120, y: 150, z: -15
          },
          {
            id: "tangent_pi_3_result",
            name: "Tangent π/3 Result",
            type: "values",
            description: "The tangent of π/3 radians",
            value: "tan(π/3) = tan(60°) = √3",
            importance: 9,
            x: 120, y: 150, z: -15
          }
        ],
        links: [
          { from: "trigonometry", to: "tangent_function", type: "includes", strength: 9 },
          { from: "trigonometry", to: "radian_measure", type: "includes", strength: 8 },
          { from: "angle_pi_3", to: "degree_equivalent", type: "converts to", strength: 8 },
          { from: "degree_equivalent", to: "tangent_pi_3_result", type: "used to calculate", strength: 9 }
        ]
      }
    },
    {
      id: 'trig_hard_04',
      problem: "Find sin(2π/3)",
      problemType: "trigonometry",
      difficulty: "harder",
      description: "Sine function with radian measure in second quadrant",
      graphData: {
        nodes: [
          {
            id: "trigonometry",
            name: "Trigonometry",
            type: "concepts",
            description: "Study of relationships between angles and sides of triangles",
            value: "Angle and side relationships",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "sine_function",
            name: "Sine Function",
            type: "concepts",
            description: "Ratio of opposite side to hypotenuse",
            value: "sin(θ) = opposite/hypotenuse",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "radian_measure",
            name: "Radian Measure",
            type: "concepts",
            description: "Angle measure in radians",
            value: "π radians = 180°",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle_2pi_3",
            name: "Angle 2π/3",
            type: "values",
            description: "The given angle in radians",
            value: "2π/3",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "degree_equivalent",
            name: "Degree Equivalent",
            type: "values",
            description: "Convert radians to degrees",
            value: "2π/3 = 120°",
            importance: 8,
            x: -120, y: 150, z: -15
          },
          {
            id: "sine_2pi_3_result",
            name: "Sine 2π/3 Result",
            type: "values",
            description: "The sine of 2π/3 radians",
            value: "sin(2π/3) = sin(120°) = √3/2",
            importance: 9,
            x: 120, y: 150, z: -15
          }
        ],
        links: [
          { from: "trigonometry", to: "sine_function", type: "includes", strength: 9 },
          { from: "trigonometry", to: "radian_measure", type: "includes", strength: 8 },
          { from: "angle_2pi_3", to: "degree_equivalent", type: "converts to", strength: 8 },
          { from: "degree_equivalent", to: "sine_2pi_3_result", type: "used to calculate", strength: 9 }
        ]
      }
    },
    {
      id: 'trig_hard_05',
      problem: "Find cos(5π/6)",
      problemType: "trigonometry",
      difficulty: "harder",
      description: "Cosine function with radian measure in second quadrant",
      graphData: {
        nodes: [
          {
            id: "trigonometry",
            name: "Trigonometry",
            type: "concepts",
            description: "Study of relationships between angles and sides of triangles",
            value: "Angle and side relationships",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cosine_function",
            name: "Cosine Function",
            type: "concepts",
            description: "Ratio of adjacent side to hypotenuse",
            value: "cos(θ) = adjacent/hypotenuse",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "radian_measure",
            name: "Radian Measure",
            type: "concepts",
            description: "Angle measure in radians",
            value: "π radians = 180°",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "angle_5pi_6",
            name: "Angle 5π/6",
            type: "values",
            description: "The given angle in radians",
            value: "5π/6",
            importance: 10,
            x: 0, y: 60, z: 35
          },
          {
            id: "degree_equivalent",
            name: "Degree Equivalent",
            type: "values",
            description: "Convert radians to degrees",
            value: "5π/6 = 150°",
            importance: 8,
            x: -120, y: 150, z: -15
          },
          {
            id: "cosine_5pi_6_result",
            name: "Cosine 5π/6 Result",
            type: "values",
            description: "The cosine of 5π/6 radians",
            value: "cos(5π/6) = cos(150°) = -√3/2",
            importance: 9,
            x: 120, y: 150, z: -15
          }
        ],
        links: [
          { from: "trigonometry", to: "cosine_function", type: "includes", strength: 9 },
          { from: "trigonometry", to: "radian_measure", type: "includes", strength: 8 },
          { from: "angle_5pi_6", to: "degree_equivalent", type: "converts to", strength: 8 },
          { from: "degree_equivalent", to: "cosine_5pi_6_result", type: "used to calculate", strength: 9 }
        ]
      }
    }
  ]
}; 